import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, Image as ImageIcon, PlayCircle, Camera, GripVertical, Plus, Mail, CheckCircle2 } from "lucide-react";
import api, { formatApiError, resolveMedia } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="block text-xs text-neutral-500 mb-1.5">{label}</span>
      {children}
      {hint && <span className="text-xs text-neutral-400 mt-1 block">{hint}</span>}
    </label>
  );
}

function Input(props) {
  return <input {...props} className={`w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))] ${props.className || ""}`} />;
}

function detectProvider(url) {
  const u = (url || "").toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
  if (u.includes("vimeo.com")) return "vimeo";
  return "";
}

export default function AdminBauchronikEdit() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", date: "", date_iso: "", description: "",
    header_image_url: "", media: [], status: "draft", order: 0,
    send_newsletter: false,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    if (isNew) return;
    api.get("/bauchroniken/all").then(({ data }) => {
      const item = (data || []).find((n) => n.id === id);
      if (item) {
        setForm({
          title: item.title || "",
          date: item.date || "",
          date_iso: item.date_iso || "",
          description: item.description || "",
          header_image_url: item.header_image_url || "",
          media: item.media || [],
          status: item.status || "draft",
          order: item.order || 0,
          send_newsletter: item.send_newsletter || false,
          _newsletter_sent_at: item.newsletter_sent_at || "",
        });
      }
    }).finally(() => setLoading(false));
  }, [id, isNew]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const uploadHeader = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await upload(file);
      setForm((f) => ({ ...f, header_image_url: url }));
      toast.success("Header-Bild hochgeladen.");
    } catch (e) {
      toast.error("Upload fehlgeschlagen: " + (formatApiError(e.response?.data?.detail) || e.message));
    } finally { setUploading(false); }
  };

  const uploadImages = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const url = await upload(file);
        uploaded.push({ type: "image", url, thumbnail_url: "", caption: "", provider: "" });
      }
      setForm((f) => ({ ...f, media: [...f.media, ...uploaded] }));
      toast.success(`${uploaded.length} Bild(er) hochgeladen.`);
    } catch (e) {
      toast.error("Upload fehlgeschlagen: " + (formatApiError(e.response?.data?.detail) || e.message));
    } finally { setUploading(false); }
  };

  const uploadVideoThumb = async (idx, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await upload(file);
      setForm((f) => {
        const media = [...f.media];
        media[idx] = { ...media[idx], thumbnail_url: url };
        return { ...f, media };
      });
    } catch (e) { toast.error("Thumbnail-Upload fehlgeschlagen."); }
    finally { setUploading(false); }
  };

  const addVideo = () => {
    setForm((f) => ({ ...f, media: [...f.media, { type: "video", url: "", thumbnail_url: "", caption: "", provider: "" }] }));
  };

  const updateMedia = (idx, patch) => {
    setForm((f) => {
      const media = [...f.media];
      media[idx] = { ...media[idx], ...patch };
      if (patch.url !== undefined && media[idx].type === "video") {
        media[idx].provider = detectProvider(patch.url);
      }
      return { ...f, media };
    });
  };

  const removeMedia = (idx) => {
    setForm((f) => ({ ...f, media: f.media.filter((_, i) => i !== idx) }));
  };

  const onDragStart = (i) => (e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetIdx) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) return;
    setForm((f) => {
      const media = [...f.media];
      const [m] = media.splice(dragIdx, 1);
      media.splice(targetIdx, 0, m);
      return { ...f, media };
    });
    setDragIdx(null);
  };

  const save = async (asStatus) => {
    const { _newsletter_sent_at, ...clean } = form;
    const body = { ...clean, status: asStatus || form.status };
    if (!body.title.trim()) { toast.error("Bitte einen Titel eintragen."); return; }
    // Validate video URLs
    for (const m of body.media) {
      if (m.type === "video") {
        const p = detectProvider(m.url);
        if (!p) { toast.error(`Ungültige Video-URL: „${m.url}". Nur YouTube- oder Vimeo-Links werden unterstützt.`); return; }
        m.provider = p;
      }
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/bauchroniken", body);
        toast.success(body.send_newsletter && body.status === "published" ? "Eintrag erstellt — Newsletter-Versand läuft." : "Eintrag erstellt.");
        nav(`/admin/bauchroniken/${data.id}`);
      } else {
        const { data } = await api.put(`/bauchroniken/${id}`, body);
        setForm({ ...body, _newsletter_sent_at: data.newsletter_sent_at || "" });
        toast.success(
          body.send_newsletter && body.status === "published" && !form._newsletter_sent_at
            ? "Gespeichert — Newsletter-Versand läuft im Hintergrund."
            : "Gespeichert."
        );
      }
    } catch (e) {
      toast.error("Speichern fehlgeschlagen: " + (formatApiError(e.response?.data?.detail) || e.message));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-neutral-500">Lade…</div>;

  return (
    <div className="space-y-8" data-testid="admin-bauchronik-edit-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/admin/bauchroniken" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-neutral-500 hover:text-neutral-800 mb-2">
            <ArrowLeft className="h-3.5 w-3.5" /> Bauchroniken
          </Link>
          <h1 className="text-3xl font-semibold text-neutral-900">{isNew ? "Neuer Bauchronik-Eintrag" : "Eintrag bearbeiten"}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => save("draft")} disabled={saving} data-testid="bauchronik-save-draft" className="border border-brand-line px-4 py-2 text-sm hover:bg-neutral-50 disabled:opacity-50">
            {saving ? "…" : "Als Entwurf speichern"}
          </button>
          <button onClick={() => save("published")} disabled={saving} data-testid="bauchronik-save-publish" className="bg-[hsl(var(--brand-green-strong))] text-white px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50">
            {saving ? "Speichere…" : "Veröffentlichen"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Prominent Newsletter dispatch card */}
          <div
            className={`border-2 ${form.send_newsletter && !form._newsletter_sent_at ? "border-[hsl(var(--brand-green-strong))] bg-[hsl(var(--brand-soft))]" : form._newsletter_sent_at ? "border-emerald-200 bg-emerald-50/50" : "border-brand-line bg-white"} p-5 sm:p-6`}
            data-testid="bauchronik-newsletter-card"
          >
            <label className="flex items-start gap-4 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.send_newsletter}
                onChange={(e) => setForm((f) => ({ ...f, send_newsletter: e.target.checked }))}
                data-testid="bauchronik-send-newsletter"
                disabled={!!form._newsletter_sent_at}
                className="mt-1 h-5 w-5 accent-[hsl(var(--brand-green-strong))] shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  {form._newsletter_sent_at ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Mail className="h-5 w-5 text-[hsl(var(--brand-green-strong))]" />}
                  <h3 className="text-base sm:text-lg font-semibold text-neutral-900">
                    {form._newsletter_sent_at ? "Newsletter wurde bereits verschickt" : "Als Newsletter verschicken?"}
                  </h3>
                </div>
                {form._newsletter_sent_at ? (
                  <p className="text-sm text-neutral-700">
                    Versendet am <strong>{new Date(form._newsletter_sent_at).toLocaleString("de-DE", { dateStyle: "long", timeStyle: "short" })}</strong>. Ein zweiter automatischer Versand ist ausgeschlossen — Sie können den Eintrag beliebig oft weiter bearbeiten.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-neutral-600 leading-relaxed">
                      Wenn Sie hier einen Haken setzen und den Eintrag <strong>veröffentlichen</strong>, wird automatisch eine E-Mail mit Header-Bild, Titel und Kurzbeschreibung an alle <strong>bestätigten Newsletter-Abonnenten</strong> versendet — genau einmal, in Batches zu je 10 Mails im Hintergrund.
                    </p>
                    <p className="text-xs text-neutral-500 mt-2">
                      Kein Versand bei Entwurf. Der Zeitpunkt des Versands wird nach dem ersten Klick auf „Veröffentlichen" fixiert — kein doppelter Versand möglich.
                    </p>
                  </>
                )}
              </div>
            </label>
          </div>

          <Field label="Titel *">
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="bauchronik-title-input" placeholder="z. B. Baustart Baugrube Nord" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Zeitraum (Anzeige)" hint={`Wird auf der Seite angezeigt (z. B. „Februar 2026", „Frühjahr 2026", „KW 12 – 14 · 2026").`}>
              <Input value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="bauchronik-date-input" placeholder="Februar 2026" />
            </Field>
            <Field label="ISO-Datum (Sortierung)" hint="Optional, für exakte Sortierung. Format: YYYY-MM-DD">
              <Input type="date" value={form.date_iso ? form.date_iso.slice(0, 10) : ""} onChange={(e) => setForm((f) => ({ ...f, date_iso: e.target.value }))} data-testid="bauchronik-date-iso-input" />
            </Field>
          </div>

          <Field label="Beschreibung" hint="Beschreiben Sie die dokumentierte Veränderung. HTML-Editor mit Fett, Kursiv, Überschriften, Listen und Links.">
            <RichTextEditor value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} minHeight={220} testid="bauchronik-description-editor" />
          </Field>

          {/* Media manager */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-neutral-500">Fotos &amp; Videos</label>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 border border-brand-line px-3 py-1.5 text-xs cursor-pointer hover:bg-neutral-50">
                  <Upload className="h-3.5 w-3.5" /> Bilder hochladen
                  <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={(e) => { uploadImages(e.target.files); e.target.value = ""; }} data-testid="bauchronik-images-upload" />
                </label>
                <button onClick={addVideo} className="inline-flex items-center gap-1.5 border border-brand-line px-3 py-1.5 text-xs hover:bg-neutral-50" data-testid="bauchronik-add-video">
                  <PlayCircle className="h-3.5 w-3.5" /> YouTube/Vimeo hinzufügen
                </button>
              </div>
            </div>
            {form.media.length === 0 ? (
              <div className="border border-dashed border-brand-line p-8 text-center text-xs text-neutral-500">
                Noch keine Medien. Videos werden ausschließlich über YouTube- oder Vimeo-URLs eingebunden.
              </div>
            ) : (
              <div className="space-y-2">
                {form.media.map((m, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={onDragStart(i)}
                    onDragOver={onDragOver}
                    onDrop={onDrop(i)}
                    className={`flex items-start gap-3 border border-brand-line bg-white p-3 ${dragIdx === i ? "opacity-50" : ""}`}
                    data-testid={`bauchronik-media-row-${i}`}
                  >
                    <GripVertical className="h-4 w-4 text-neutral-300 mt-1 cursor-grab shrink-0" />
                    <div className="w-16 h-16 bg-neutral-100 border border-brand-line overflow-hidden shrink-0">
                      {m.type === "image" && m.url ? (
                        <img src={resolveMedia(m.url)} alt="" className="w-full h-full object-cover" />
                      ) : m.type === "video" && m.thumbnail_url ? (
                        <img src={resolveMedia(m.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-300">
                          {m.type === "video" ? <PlayCircle className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-neutral-500">
                        {m.type === "image" ? (<><Camera className="h-3 w-3" /> Bild</>) : (<><PlayCircle className="h-3 w-3" /> Video {m.provider && `· ${m.provider}`}</>)}
                      </div>
                      {m.type === "video" ? (
                        <>
                          <Input value={m.url} onChange={(e) => updateMedia(i, { url: e.target.value })} placeholder="https://www.youtube.com/watch?v=… oder https://vimeo.com/…" data-testid={`bauchronik-media-video-url-${i}`} />
                          <div className="flex items-center gap-2">
                            <label className="inline-flex items-center gap-1.5 text-xs text-neutral-600 border border-brand-line px-2 py-1 cursor-pointer hover:bg-neutral-50">
                              <Upload className="h-3 w-3" /> Vorschaubild
                              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadVideoThumb(i, e.target.files?.[0])} data-testid={`bauchronik-media-thumb-${i}`} />
                            </label>
                            {m.thumbnail_url && (
                              <button onClick={() => updateMedia(i, { thumbnail_url: "" })} className="text-xs text-neutral-500 hover:text-red-600">Thumb entfernen</button>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-neutral-400 font-mono truncate">{m.url}</div>
                      )}
                      <Input value={m.caption || ""} onChange={(e) => updateMedia(i, { caption: e.target.value })} placeholder="Optionale Bildunterschrift" data-testid={`bauchronik-media-caption-${i}`} />
                    </div>
                    <button onClick={() => removeMedia(i)} className="text-neutral-400 hover:text-red-600 p-1 shrink-0" title="Entfernen" data-testid={`bauchronik-media-remove-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="bauchronik-status-select" className="w-full border border-brand-line px-3 py-2.5 text-sm bg-white">
              <option value="draft">Entwurf</option>
              <option value="published">Veröffentlicht</option>
            </select>
          </Field>

          <div>
            <label className="block text-xs text-neutral-500 mb-1.5">Header-Bild des Eintrags</label>
            <div className="aspect-[16/9] bg-neutral-100 border border-brand-line overflow-hidden">
              {form.header_image_url ? (
                <img src={resolveMedia(form.header_image_url)} alt="Header" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs"><ImageIcon className="h-6 w-6" /></div>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 border border-brand-line px-3 py-1.5 text-xs cursor-pointer hover:bg-neutral-50">
                <Upload className="h-3.5 w-3.5" /> {form.header_image_url ? "Austauschen" : "Hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadHeader(e.target.files?.[0])} data-testid="bauchronik-header-upload" />
              </label>
              {form.header_image_url && (
                <button onClick={() => setForm((f) => ({ ...f, header_image_url: "" }))} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Entfernen</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
